// src/seedDemoData.js
import { collection, doc, setDoc, getDocs } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Normalize Amazon URL for comparison
 */
const normalizeAmazonUrl = (url) => {
  if (!url) return '';
  
  // Extract ASIN/product ID from Amazon URL
  const asinMatch = url.match(/\/dp\/([A-Z0-9]{10})|\/gp\/product\/([A-Z0-9]{10})/i);
  if (asinMatch) {
    return (asinMatch[1] || asinMatch[2]).toUpperCase();
  }
  
  // Fallback: normalize the URL
  return url.toLowerCase()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '')
    .split('?')[0]
    .split('#')[0];
};

/**
 * Check if a book with the same Amazon URL already exists
 */
export const checkDuplicateBookURL = async (amazonLink) => {
  try {
    const normalizedNewUrl = normalizeAmazonUrl(amazonLink);
    
    // Get all books
    const booksSnapshot = await getDocs(collection(db, 'books'));
    
    // Check each book's URL
    for (const bookDoc of booksSnapshot.docs) {
      const book = bookDoc.data();
      const normalizedExistingUrl = normalizeAmazonUrl(book.amazonLink);
      
      if (normalizedExistingUrl === normalizedNewUrl) {
        return {
          isDuplicate: true,
          message: `This book already exists:\n\n"${book.title}" by ${book.author}`,
          existingBook: { id: bookDoc.id, ...book }
        };
      }
    }
    
    return { isDuplicate: false };
  } catch (error) {
    console.error('Error checking duplicate book URL:', error);
    return { isDuplicate: false, error: error.message };
  }
};

/**
 * Seed demo books if database is empty
 */
export const seedDemoBooks = async () => {
  try {
    // Check if books already exist
    const booksSnapshot = await getDocs(collection(db, 'books'));
    
    if (booksSnapshot.docs.length > 0) {
      console.log('📚 Books already exist. Skipping seed.');
      return { success: true, skipped: true };
    }
    
    console.log('📚 Seeding demo books...');
    
    const demoBooks = [
      {
        title: 'The Midnight Library',
        author: 'Matt Haig',
        genre: 'Fiction',
        description: 'A dazzling novel about possibilities',
        pageCount: 288,
        amazonLink: 'https://www.amazon.com/dp/0525559477',
        coinsRequired: 15,
        isPromoted: false,
        coverImage: '',
        keywords: 'philosophy, choices, parallel lives',
        mainCharacters: 'Nora Seed',
        targetCountries: ['US', 'UK', 'CA'],
        uploadedBy: 'demo-author-1',
        reviewsReceived: 0,
        visibilityEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        title: 'Project Hail Mary',
        author: 'Andy Weir',
        genre: 'Science Fiction',
        description: 'A lone astronaut saves Earth',
        pageCount: 476,
        amazonLink: 'https://www.amazon.com/dp/0593135202',
        coinsRequired: 20,
        isPromoted: false,
        coverImage: '',
        keywords: 'space, science, survival',
        mainCharacters: 'Ryland Grace',
        targetCountries: ['US', 'UK'],
        uploadedBy: 'demo-author-2',
        reviewsReceived: 0,
        visibilityEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];
    
    // Add books to Firestore
    for (const book of demoBooks) {
      const bookRef = doc(collection(db, 'books'));
      await setDoc(bookRef, {
        ...book,
        id: bookRef.id
      });
      console.log(`✅ Added: ${book.title}`);
    }
    
    console.log('✅ Demo books seeded successfully!');
    return { success: true, count: demoBooks.length };
  } catch (error) {
    console.error('Error seeding demo books:', error);
    return { success: false, error: error.message };
  }
};